import { useEffect, useId, useRef, useState } from 'react';
import { systemOptionValues } from '@/domain/betslip';
import { cn } from '@/lib/cn';
import { Icon } from '../common/icon';

export interface BetslipSystemOptionsProps {
  /** Chosen fold size `k` (`SystemMin`). */
  value: number;
  /** Unbanked count `n` (`SystemMax`). */
  max: number;
  /** BetSettings.MinimumSystem fold floor. */
  minSystem?: number;
  onChange?: (value: number) => void;
  className?: string;
}

/**
 * Figma BetslipSystemOptions — closed/open `k/n` system type dropdown.
 */
export function BetslipSystemOptions({
  value,
  max,
  minSystem,
  onChange,
  className,
}: BetslipSystemOptionsProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const listId = useId();
  const options = systemOptionValues(max, { minSystem });
  const label = max > 0 ? `${value}/${max}` : '—';

  useEffect(() => {
    if (!open) return;

    const onDocumentClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  if (options.length === 0) return null;

  return (
    <div ref={rootRef} className={cn('relative flex w-full flex-col gap-1', className)}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label="System type"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex h-11 w-full items-center justify-between overflow-hidden rounded-md bg-bg-surface px-4 py-3',
          open
            ? 'border-2 border-action-primary'
            : 'border border-border-subtle',
        )}
      >
        <span className="min-w-0 flex-1 text-left text-body-m font-bold text-text-primary">
          {label}
        </span>
        <Icon name="chevron-down" size={14} className="shrink-0 text-text-primary" />
      </button>

      {open ? (
        <ul
          id={listId}
          role="listbox"
          aria-label="System type options"
          className="absolute top-[calc(100%+4px)] right-0 left-0 z-30 flex flex-col overflow-hidden rounded-md border border-border-subtle bg-bg-surface py-1"
        >
          {options.map((k) => {
            const selected = k === value;
            return (
              <li key={k} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    onChange?.(k);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center px-4 py-2.5 text-left text-body-m text-text-primary',
                    selected
                      ? 'bg-action-primary font-bold text-text-on-action'
                      : 'font-normal hover:bg-bg-elevated',
                  )}
                >
                  {`${k}/${max}`}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
